import React, { Fragment } from 'react';
import axios from 'axios';
import qs from 'qs';
import { connect } from 'react-redux';
import { withSnackbar } from 'notistack';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';
import RewardCard from '../parts/RewardCard';

const styles = () => ({
  balanceCard: {
    backgroundColor: '#39843b',
    color: 'rgba(255, 255, 255, 0.8)',
    padding: 20,
    textAlign: 'center',
    lineHeight: 1.3,
  },
  balanceCardTopText: {
    fontSize: 20,
  },
  balanceCardMiddleText: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#fff',
  },
  balanceCardBottomText: {
    fontSize: 20,
    textTransform: 'uppercase',
  },
});

const mapStateToProps = state => {
  return {
    selectedPupil: state.selectedPupil,
    defaultDateRange: state.defaultDateRange,
  };
};

class RewardsPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      data: [],
      isLoading: false,
      isLoaded: false,
      meta: [],
      isDialogOpen: false,
      purchaseInProgress: false,
      dialogText: '',
      dialogData: {},
    };

    this.axiosCancel = null;

    this.onPurchaseButtonClick = this.onPurchaseButtonClick.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.onDialogConfirm = this.onDialogConfirm.bind(this);
  }

  componentDidMount() {
    this.reloadData();
  }

  componentWillUnmount() {
    if (this.axiosCancel) {
      this.axiosCancel();
    }
  }

  reloadData() {
    const {
      selectedPupil,
    } = this.props;

    if (!selectedPupil) {
      return;
    }

    this.setState({
      isLoading: true,
      isLoaded: false,
    });

    axios
      .get(
        `${window.apiUrl}/rewards/${selectedPupil.id}`,
        {
          cancelToken: new axios.CancelToken((c) => { this.axiosCancel = c }),
        }
      )
      .then((response) => {
        if (response.data.success) {
          this.setState({
            data: response.data.data,
            meta: response.data.meta,
          });
        }

        this.setState({
          isLoading: false,
          isLoaded: true,
        });
      })
      .catch((thrown) => {});
  }

  onPurchaseButtonClick(reward) {
    const {
      cookies,
      enqueueSnackbar,
      t,
    } = this.props;

    const text = t('Are you sure you want to spend {price} points to buy {name}?')
      .replace('{price}', reward.price)
      .replace('{name}', reward.name);

    this.setState({
      isDialogOpen: true,
      purchaseInProgress: false,
      dialogText: text,
      dialogData: reward,
    });
  }

  onDialogClose() {
    this.setState({
      isDialogOpen: false,
    });
  }

  onDialogConfirm() {
    const {
      enqueueSnackbar,
      t,
      selectedPupil,
    } = this.props;

    if (this.state.purchaseInProgress) {
      return;
    }

    this.setState({
      purchaseInProgress: true,
    });

    axios
      .post(`${window.apiUrl}/purchase/${this.state.dialogData.id}`, qs.stringify({
        pupil_id: selectedPupil.id,
      }))
      .then((response) => {
        if (response.data.success) {
          enqueueSnackbar(t('You have successfully purchased the reward.'), {
            variant: 'success',
            autoHideDuration: 2000,
          });

          this.reloadData();
          this.onDialogClose();
        } else {
          enqueueSnackbar(response.data.error, {
            variant: 'error',
            autoHideDuration: 2000,
          });
        }

        // Make sure the dialog has closed,
        //   else the button won't be disabled for a split second,
        //   and you'll be able to click on it.
        setTimeout(() => {
          this.setState({
            purchaseInProgress: false,
          });
        }, 500);
      });
  }

  render() {
    const {
      classes,
      t,
    } = this.props;
    const {
      data,
      isLoaded,
      isLoading,
      isDialogOpen,
      purchaseInProgress,
      dialogText,
      meta,
    } = this.state;

    return (
      <div className="rewards-page">
        {isLoading && !isLoaded && (
          <CircularProgress />
        )}
        {!isLoading && isLoaded && (
          <Grid
            justify="space-between"
            container
            spacing={4}
          >
            <Grid
              item
              xs={12}
            >
              <Paper
                elevation={1}
                className={classes.balanceCard}
              >
                <div className={classes.balanceCardTopText}>
                  {t('Your balance')}
                </div>
                <div className={classes.balanceCardMiddleText}>
                  {meta.pupil_score_balance}
                </div>
                <div className={classes.balanceCardBottomText}>
                  {t('Points')}
                </div>
              </Paper>
            </Grid>
            {data.map(reward => (
              <Grid
                item
                key={reward.id}
                xs={12}
                sm={6}
                md={4}
              >
                <RewardCard
                  reward={reward}
                  onPurchaseButtonClick={this.onPurchaseButtonClick}
                  useSuspense={false}
                />
              </Grid>
            ))}
          </Grid>
        )}
        <Dialog
          open={isDialogOpen}
          onClose={this.onDialogClose}
          aria-labelledby="purchase-dialog-title"
          aria-describedby="purchase-dialog-description"
          className="purchase-dialog"
        >
          <DialogTitle id="purchase-dialog-title">
            {t('Purchase confirmation')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="purchase-dialog-description">
              {dialogText}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.onDialogClose}
              color="primary"
              className="purchase-dialog-no-button"
            >
              {t('No')}
            </Button>
            <Button
              onClick={this.onDialogConfirm}
              color="primary"
              autoFocus
              disabled={purchaseInProgress}
              className="purchase-dialog-yes-button"
            >
              {t('Yes')}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default connect(mapStateToProps)(
  withStyles(styles)(withSnackbar(withTranslation()(RewardsPage)))
);
